import {
  type InterviewStage,
  type OfferApprovalRules,
  type StageFeedbackCriterion,
  stageFeedbackCriterionSchema,
} from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowUp,
  Bell,
  ChevronDown,
  ChevronRight,
  Clock,
  Crown,
  Eye,
  EyeOff,
  FileCheck,
  HelpCircle,
  Plus,
  Search,
  Sparkles,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { OfferApprovalRulesEditor } from "@/components/offer-approval-rules-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";
import type { PersonOption } from "@/types/move-application";

// Re-export for backwards compat
export interface SettingToggle {
  checked: boolean;
  onToggle: () => void;
  isPending: boolean;
  testId: string;
}

interface OfferApprovalSetting {
  rules: OfferApprovalRules | null;
  onSave: (rules: OfferApprovalRules | null) => void;
  isPending: boolean;
}

export interface DigestScheduleEntry {
  id: string;
  jobOpeningId: string;
  hour: number;
  createdAt: Date | null;
}

export interface DigestSettingToggle extends SettingToggle {
  schedule: DigestScheduleEntry[];
  isSchedulePending: boolean;
  onAddHour: (hour: number) => void;
  onDeleteEntry: (id: string) => void;
  onApplyToAll: () => void;
  isApplyToAllPending: boolean;
  hasMultipleJobs: boolean;
}

export type EditJobFormValues = {
  title: string;
  generalResponsibilities: string;
  desiredProfile: string;
  compensation: string;
  jobLevel: string;
  department: string;
  hiringManagerId: string;
  salaryExpected: string;
};

export type EditableStage = {
  id?: string;
  stageName: string;
  stageOrder: number;
  durationMinutes: number | "";
  defaultInterviewerId: string | null;
  interviewerIds: string[];
  interviewerGroups: { id?: string; employeeIds: string[]; schedulingLink: string | null }[];
  hidden: boolean;
  materials: string | null;
  description: string | null;
  /** Feedback form template used by the stage; null = default Comp form. */
  feedbackFormId: string | null;
};

/** Local editing state for one feedback form template. */
type EditableFeedbackForm = {
  id?: string;
  name: string;
  criteria: StageFeedbackCriterion[];
};

interface JobSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?:
    | "edit-job"
    | "edit-stages"
    | "feedback-forms"
    | "settings"
    | "offer-approval"
    | "access";
  onTabChange?: (
    tab: "edit-job" | "edit-stages" | "feedback-forms" | "settings" | "offer-approval" | "access",
  ) => void;
  isSuperAdmin?: boolean;
  jobOpeningId: string;

  // -- Configurações tab (settings toggles) --
  careersVisibility: SettingToggle;
  autoAdvance: SettingToggle;
  feedbackAlert: SettingToggle;
  dailyDigest: DigestSettingToggle;
  newApplicationAlert: SettingToggle;
  offerApproval: OfferApprovalSetting;

  // -- Editar vaga tab --
  editJobForm: UseFormReturn<EditJobFormValues>;
  onUpdateJob: (data: EditJobFormValues) => void;
  updateJobIsPending: boolean;
  people: PersonOption[] | undefined;
  departments: { value: string; label: string }[];
  jobLevels: { value: string; label: string }[];

  // -- Editar etapas tab --
  editableStages: EditableStage[];
  setEditableStages: React.Dispatch<React.SetStateAction<EditableStage[]>>;
  expandedStages: Record<number, boolean>;
  setExpandedStages: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  editStageInterviewerSearch: Record<number, string>;
  setEditStageInterviewerSearch: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  onUpdateStages: (stages: EditableStage[]) => void;
  updateStagesIsPending: boolean;
  onValidateAndSaveStages: () => void;

  // Archive stage flow
  archiveDialogStage: { id: string; name: string } | null;
  setArchiveDialogStage: React.Dispatch<React.SetStateAction<{ id: string; name: string } | null>>;
  archiveAction: "keep" | "migrate";
  setArchiveAction: React.Dispatch<React.SetStateAction<"keep" | "migrate">>;
  archiveTargetStageId: string;
  setArchiveTargetStageId: React.Dispatch<React.SetStateAction<string>>;
  onArchiveStage: (params: {
    stageId: string;
    action: "keep" | "migrate";
    targetStageId?: string;
  }) => void;
  onArchiveInfoCheck: (stageId: string) => void;
  archiveInfoMutation: {
    isPending: boolean;
    data?: { interviewCount: number; applicationCount: number };
    reset: () => void;
  };
  deletingStageId: string | null;

  // Archived stages on board
  archivedInterviewStages: InterviewStage[];
  onUnarchiveStage: (stageId: string) => void;
  unarchiveIsPending: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function JobSettingsDialog({
  open,
  onOpenChange,
  defaultTab = "settings",
  onTabChange,
  isSuperAdmin = false,
  jobOpeningId,
  careersVisibility,
  autoAdvance,
  feedbackAlert,
  dailyDigest,
  newApplicationAlert,
  offerApproval,
  editJobForm,
  onUpdateJob,
  updateJobIsPending,
  people,
  departments,
  jobLevels,
  editableStages,
  setEditableStages,
  expandedStages,
  setExpandedStages,
  editStageInterviewerSearch,
  setEditStageInterviewerSearch,
  onUpdateStages: _onUpdateStages,
  updateStagesIsPending,
  onValidateAndSaveStages,
  archiveDialogStage,
  setArchiveDialogStage,
  archiveAction,
  setArchiveAction,
  archiveTargetStageId,
  setArchiveTargetStageId,
  onArchiveStage,
  onArchiveInfoCheck,
  archiveInfoMutation,
  deletingStageId,
  archivedInterviewStages,
  onUnarchiveStage,
  unarchiveIsPending,
}: JobSettingsDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ── Hiring managers ───────────────────────────────────────────────────────
  const hmQueryOptions = orpc.jobOpenings.listHiringManagers.queryOptions({ id: jobOpeningId });
  const { data: hiringManagers = [], isLoading: hmLoading } = useQuery({
    ...hmQueryOptions,
    enabled: open,
  });

  const { data: hmPeople = [] } = useQuery({
    ...orpc.dashboard.people.queryOptions(),
    enabled: open,
  });

  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [removingEmployeeId, setRemovingEmployeeId] = useState<string | null>(null);

  // ── Feedback form templates ───────────────────────────────────────────────
  const feedbackFormsQueryOptions = orpc.jobOpenings.listFeedbackForms.queryOptions({
    input: { id: jobOpeningId },
  });
  const { data: feedbackFormsData } = useQuery({
    ...feedbackFormsQueryOptions,
    enabled: open,
  });
  const feedbackForms = feedbackFormsData ?? [];

  const [editableForms, setEditableForms] = useState<EditableFeedbackForm[]>([]);
  // Initialize the editing state once per dialog open (when the data arrives).
  // Depending on the query data directly would wipe in-progress edits on any
  // background refetch — and loop while the query is pending.
  const formsInitializedRef = useRef(false);
  useEffect(() => {
    if (!open) formsInitializedRef.current = false;
  }, [open]);
  useEffect(() => {
    if (open && feedbackFormsData && !formsInitializedRef.current) {
      formsInitializedRef.current = true;
      setEditableForms(
        feedbackFormsData.map((f) => ({ id: f.id, name: f.name, criteria: f.criteria })),
      );
    }
  }, [open, feedbackFormsData]);

  const updateForm = (fIdx: number, patch: Partial<EditableFeedbackForm>) => {
    setEditableForms((prev) => prev.map((f, i) => (i === fIdx ? { ...f, ...patch } : f)));
  };

  const updateFormCriterion = (
    fIdx: number,
    cIdx: number,
    patch: Partial<StageFeedbackCriterion>,
  ) => {
    setEditableForms((prev) =>
      prev.map((f, i) =>
        i === fIdx
          ? {
              ...f,
              criteria: f.criteria.map((c, j) =>
                j === cIdx ? ({ ...c, ...patch } as StageFeedbackCriterion) : c,
              ),
            }
          : f,
      ),
    );
  };

  const saveFormsMutation = useMutation({
    mutationFn: async () => {
      // Drop criteria rows whose label was never filled
      const forms = editableForms.map((f) => ({
        ...(f.id ? { id: f.id } : {}),
        name: f.name.trim(),
        criteria: f.criteria.filter((c) => c.label.trim().length > 0),
      }));
      return rpcClient.jobOpenings.saveFeedbackForms({ id: jobOpeningId, forms });
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: feedbackFormsQueryOptions.queryKey });
      // Re-sync the editor with the persisted forms (new rows get their ids)
      setEditableForms(saved.map((f) => ({ id: f.id, name: f.name, criteria: f.criteria })));
      // Stages still pointing at a deleted form would fail the stage save (the
      // DB already SET-NULL'd them) — clear the local references too.
      const savedIds = new Set(saved.map((f) => f.id));
      setEditableStages((prev) =>
        prev.map((s) =>
          s.feedbackFormId && !savedIds.has(s.feedbackFormId) ? { ...s, feedbackFormId: null } : s,
        ),
      );
      toast({
        title: "Formulários salvos",
        description: "Escolha qual etapa usa cada formulário na aba Editar etapas.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao salvar formulários",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const validateAndSaveForms = () => {
    for (const form of editableForms) {
      if (!form.name.trim() || form.name.trim().length > 80) {
        toast({
          title: "Nome inválido",
          description: "Todo formulário precisa de um nome com até 80 caracteres.",
          variant: "destructive",
        });
        return;
      }
      const filled = form.criteria.filter((c) => c.label.trim().length > 0);
      if (filled.length === 0) {
        toast({
          title: "Campo obrigatório",
          description: `O formulário "${form.name}" precisa de pelo menos um critério.`,
          variant: "destructive",
        });
        return;
      }
      // Same zod the server enforces — keeps the rules from drifting.
      for (const c of filled) {
        const parsed = stageFeedbackCriterionSchema.safeParse(c);
        if (!parsed.success) {
          toast({
            title: "Critério inválido",
            description: `"${c.label}": ${parsed.error.issues[0]?.message ?? "valor inválido"}`,
            variant: "destructive",
          });
          return;
        }
      }
    }
    saveFormsMutation.mutate();
  };

  const addHmMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      await rpcClient.jobOpenings.addHiringManager({ id: jobOpeningId, employeeId });
    },
    onMutate: () => {
      setSelectedEmployeeId("");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hmQueryOptions.queryKey });
      toast({ title: "Hiring manager adicionado" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao adicionar hiring manager",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const removeHmMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      await rpcClient.jobOpenings.removeHiringManager({ id: jobOpeningId, employeeId });
    },
    onMutate: (employeeId: string) => {
      setRemovingEmployeeId(employeeId);
    },
    onSuccess: () => {
      setRemovingEmployeeId(null);
      queryClient.invalidateQueries({ queryKey: hmQueryOptions.queryKey });
      toast({ title: "Hiring manager removido" });
    },
    onError: (error: Error) => {
      setRemovingEmployeeId(null);
      toast({
        title: "Erro ao remover hiring manager",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const availablePeople = hmPeople.filter(
    (p) => !hiringManagers.some((hm) => hm.employeeId === p.id),
  );

  // ── External access ───────────────────────────────────────────────────────
  const extQueryOptions = orpc.jobOpenings.listExternalAccess.queryOptions({ id: jobOpeningId });
  const { data: externalEntries = [], isLoading: extLoading } = useQuery({
    ...extQueryOptions,
    enabled: open,
  });

  const [extEmail, setExtEmail] = useState("");
  const [extEmailError, setExtEmailError] = useState("");
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null);

  const addExtMutation = useMutation({
    mutationFn: async (email: string) => {
      await rpcClient.jobOpenings.addExternalAccess({ id: jobOpeningId, email });
    },
    onMutate: () => {
      setExtEmail("");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: extQueryOptions.queryKey });
      toast({ title: "Acesso concedido com sucesso" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao conceder acesso",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const revokeExtMutation = useMutation({
    mutationFn: async (userId: string) => {
      await rpcClient.jobOpenings.revokeExternalAccess({ id: jobOpeningId, userId });
    },
    onMutate: (userId: string) => {
      setRevokingUserId(userId);
    },
    onSuccess: () => {
      setRevokingUserId(null);
      queryClient.invalidateQueries({ queryKey: extQueryOptions.queryKey });
      toast({ title: "Acesso revogado" });
    },
    onError: (error: Error) => {
      setRevokingUserId(null);
      toast({
        title: "Erro ao revogar acesso",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAddExt = () => {
    const trimmed = extEmail.trim();
    if (!trimmed) return;
    if (!EMAIL_REGEX.test(trimmed)) {
      setExtEmailError("Email inválido");
      return;
    }
    setExtEmailError("");
    addExtMutation.mutate(trimmed);
  };

  const [draftRules, setDraftRules] = useState<OfferApprovalRules | null>(offerApproval.rules);

  // Sync draft when dialog opens or external rules change
  useEffect(() => {
    setDraftRules(offerApproval.rules);
  }, [offerApproval.rules]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-4xl h-[90vh] flex flex-col overflow-hidden"
          data-testid="dialog-job-settings"
        >
          <DialogHeader>
            <DialogTitle>Configurações da Vaga</DialogTitle>
          </DialogHeader>
          <Tabs
            value={defaultTab}
            onValueChange={(v) =>
              onTabChange?.(
                v as
                  | "edit-job"
                  | "edit-stages"
                  | "feedback-forms"
                  | "settings"
                  | "offer-approval"
                  | "access",
              )
            }
            className="flex-1 flex flex-col overflow-hidden"
          >
            <TabsList className="justify-start">
              <TabsTrigger value="edit-job">Editar vaga</TabsTrigger>
              <TabsTrigger value="edit-stages">Editar etapas</TabsTrigger>
              <TabsTrigger value="feedback-forms">Formulários de feedback</TabsTrigger>
              <TabsTrigger value="settings">Configurações</TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger value="offer-approval">Aprovação de Oferta</TabsTrigger>
              )}
              <TabsTrigger value="access">Acessos</TabsTrigger>
            </TabsList>

            {/* ===== Editar vaga tab ===== */}
            <TabsContent
              value="edit-job"
              className="flex-1 overflow-hidden mt-2 focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <Form {...editJobForm}>
                <form
                  onSubmit={editJobForm.handleSubmit((data) => onUpdateJob(data))}
                  className="h-full flex flex-col"
                >
                  <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                    <FormField
                      control={editJobForm.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Título da Vaga</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-edit-job-title" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={editJobForm.control}
                      name="generalResponsibilities"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Responsabilidades</FormLabel>
                          <FormControl>
                            <Textarea
                              className="min-h-[120px] flex-1 resize-none"
                              {...field}
                              data-testid="input-edit-job-responsibilities"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={editJobForm.control}
                      name="desiredProfile"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Perfil Desejado</FormLabel>
                          <FormControl>
                            <Textarea
                              className="min-h-[120px] flex-1 resize-none"
                              {...field}
                              data-testid="input-edit-job-desired-profile"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={editJobForm.control}
                      name="compensation"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Remuneração</FormLabel>
                          <FormControl>
                            <Textarea
                              className="min-h-[80px] flex-1 resize-none"
                              {...field}
                              data-testid="input-edit-job-compensation"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={editJobForm.control}
                        name="department"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Departamento</FormLabel>
                            <SearchableSelect
                              value={field.value}
                              onValueChange={field.onChange}
                              options={departments.map((dept) => ({
                                value: dept.value,
                                label: dept.label,
                              }))}
                              placeholder="Selecione o departamento"
                              data-testid="select-edit-job-department"
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editJobForm.control}
                        name="jobLevel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nível</FormLabel>
                            <SearchableSelect
                              value={field.value}
                              onValueChange={field.onChange}
                              options={jobLevels.map((level) => ({
                                value: level.value,
                                label: level.label,
                              }))}
                              placeholder="Selecione o nível"
                              data-testid="select-edit-job-level"
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={editJobForm.control}
                        name="hiringManagerId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Hiring Manager</FormLabel>
                            <SearchableSelect
                              value={field.value}
                              onValueChange={field.onChange}
                              options={(people || []).map((person) => ({
                                value: person.id,
                                label: person.fullName,
                              }))}
                              placeholder="Selecione o responsável"
                              data-testid="select-edit-job-manager"
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editJobForm.control}
                        name="salaryExpected"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Salário Esperado (R$)</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} data-testid="input-edit-job-salary" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      data-testid="button-cancel-edit-job"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={updateJobIsPending}
                      data-testid="button-save-edit-job"
                    >
                      {updateJobIsPending ? "Salvando..." : "Salvar"}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </TabsContent>

            {/* ===== Editar etapas tab ===== */}
            <TabsContent
              value="edit-stages"
              className="flex-1 overflow-y-auto mt-2 focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <div className="space-y-3">
                {editableStages.map((stage, index) => (
                  <Card
                    key={stage.id || `new-${index}`}
                    className={`overflow-hidden ${stage.hidden ? "opacity-50 bg-muted/30" : ""}`}
                    data-testid={`edit-stage-row-${index}`}
                  >
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50">
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={index === 0}
                          onClick={() => {
                            const updated = [...editableStages];
                            [updated[index - 1], updated[index]] = [
                              updated[index],
                              updated[index - 1],
                            ];
                            setEditableStages(updated);
                          }}
                          data-testid={`button-move-stage-up-${index}`}
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                          {index + 1}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={index === editableStages.length - 1}
                          onClick={() => {
                            const updated = [...editableStages];
                            [updated[index], updated[index + 1]] = [
                              updated[index + 1],
                              updated[index],
                            ];
                            setEditableStages(updated);
                          }}
                          data-testid={`button-move-stage-down-${index}`}
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex-1 flex items-center gap-3">
                        <Input
                          value={stage.stageName}
                          onChange={(e) => {
                            const updated = [...editableStages];
                            updated[index] = { ...updated[index], stageName: e.target.value };
                            setEditableStages(updated);
                          }}
                          placeholder="Nome da etapa"
                          className="h-8 text-sm font-medium max-w-[200px]"
                          data-testid={`input-edit-stage-name-${index}`}
                        />
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          <Input
                            type="text"
                            inputMode="numeric"
                            value={stage.durationMinutes}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\D/g, "");
                              const updated = [...editableStages];
                              updated[index] = {
                                ...updated[index],
                                durationMinutes: raw === "" ? "" : parseInt(raw, 10),
                              };
                              setEditableStages(updated);
                            }}
                            onBlur={() => {
                              if (!stage.durationMinutes) {
                                const updated = [...editableStages];
                                updated[index] = { ...updated[index], durationMinutes: 30 };
                                setEditableStages(updated);
                              }
                            }}
                            className="h-8 w-16 text-sm"
                            data-testid={`input-edit-stage-duration-${index}`}
                          />
                          <span className="text-xs text-muted-foreground">min</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() =>
                            setExpandedStages((prev) => ({ ...prev, [index]: !prev[index] }))
                          }
                          data-testid={`button-expand-stage-${index}`}
                        >
                          {expandedStages[index] ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => {
                                  const updated = [...editableStages];
                                  updated[index] = {
                                    ...updated[index],
                                    hidden: !updated[index].hidden,
                                  };
                                  setEditableStages(updated);
                                }}
                                data-testid={`button-toggle-stage-hidden-${index}`}
                              >
                                {stage.hidden ? (
                                  <EyeOff className="w-3.5 h-3.5" />
                                ) : (
                                  <Eye className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {stage.hidden ? "Tornar visível" : "Esconder etapa"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          disabled={
                            stage.id
                              ? deletingStageId === stage.id || archiveInfoMutation.isPending
                              : false
                          }
                          onClick={() => {
                            if (stage.id) {
                              onArchiveInfoCheck(stage.id);
                            } else {
                              setEditableStages(editableStages.filter((_, i) => i !== index));
                            }
                          }}
                          data-testid={`button-remove-stage-${index}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    {stage.hidden && stage.id && (
                      <p className="text-xs text-muted-foreground px-4 py-2 bg-muted/20">
                        Etapa escondida — dados preservados
                      </p>
                    )}
                    {expandedStages[index] && (
                      <div className="px-4 py-3 space-y-4">
                        <div>
                          <Label className="text-xs font-medium">Descrição para o candidato</Label>
                          <p className="text-xs text-muted-foreground mb-1.5">
                            Informações que serão incluídas no email de agendamento (ex: "traga seu
                            portfólio", "será um live coding de 45min")
                          </p>
                          <Textarea
                            value={stage.description || ""}
                            onChange={(e) => {
                              const updated = [...editableStages];
                              updated[index] = {
                                ...updated[index],
                                description: e.target.value || null,
                              };
                              setEditableStages(updated);
                            }}
                            placeholder="Ex: Traga seu portfólio para apresentação. Será uma sessão de live coding de 45 minutos."
                            className="min-h-[60px] text-sm"
                            data-testid={`input-edit-stage-description-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Entrevistadores</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start font-normal mt-1"
                                data-testid={`select-edit-stage-interviewers-${index}`}
                              >
                                <Users className="h-4 w-4 mr-2 shrink-0" />
                                {stage.interviewerIds.length === 0
                                  ? "Selecionar..."
                                  : `${stage.interviewerIds.length} selecionado(s)`}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-0" align="start">
                              <div className="p-2 border-b">
                                <div className="flex items-center gap-2 px-2">
                                  <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                                  <input
                                    type="text"
                                    placeholder="Buscar entrevistador..."
                                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                    value={editStageInterviewerSearch[index] || ""}
                                    onChange={(e) =>
                                      setEditStageInterviewerSearch((prev) => ({
                                        ...prev,
                                        [index]: e.target.value,
                                      }))
                                    }
                                    data-testid={`input-search-edit-interviewers-${index}`}
                                  />
                                </div>
                              </div>
                              <div className="max-h-48 overflow-y-auto p-1">
                                {people
                                  ?.filter(
                                    (p) =>
                                      !editStageInterviewerSearch[index] ||
                                      p.fullName
                                        .toLowerCase()
                                        .includes(
                                          (editStageInterviewerSearch[index] || "").toLowerCase(),
                                        ),
                                  )
                                  .map((person) => (
                                    <label
                                      key={person.id}
                                      className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                                    >
                                      <Checkbox
                                        checked={stage.interviewerIds.includes(person.id)}
                                        onCheckedChange={(checked) => {
                                          const updated = [...editableStages];
                                          if (checked) {
                                            updated[index] = {
                                              ...updated[index],
                                              interviewerIds: [...stage.interviewerIds, person.id],
                                            };
                                          } else {
                                            updated[index] = {
                                              ...updated[index],
                                              interviewerIds: stage.interviewerIds.filter(
                                                (id) => id !== person.id,
                                              ),
                                            };
                                          }
                                          setEditableStages(updated);
                                        }}
                                        data-testid={`checkbox-edit-interviewer-${index}-${person.id}`}
                                      />
                                      <span className="text-sm flex-1">{person.fullName}</span>
                                      {(!person.schedulingLinks ||
                                        person.schedulingLinks.length === 0) && (
                                        <AlertTriangle className="h-3 w-3 text-[hsl(var(--amber-state))] shrink-0" />
                                      )}
                                    </label>
                                  ))}
                                {(!people || people.length === 0) && (
                                  <p className="text-sm text-muted-foreground p-2">
                                    Nenhum colaborador disponível
                                  </p>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Grupos de Entrevistadores</Label>
                          <div className="space-y-2 mt-1">
                            {(stage.interviewerGroups || []).map((group, gIdx) => (
                              <Card key={group.id ?? gIdx} className="p-3 bg-muted/30">
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium">Grupo {gIdx + 1}</span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                      onClick={() => {
                                        const updated = [...editableStages];
                                        updated[index] = {
                                          ...updated[index],
                                          interviewerGroups: updated[
                                            index
                                          ].interviewerGroups.filter((_, i) => i !== gIdx),
                                        };
                                        setEditableStages(updated);
                                      }}
                                      data-testid={`button-remove-edit-group-${index}-${gIdx}`}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start font-normal text-xs"
                                        data-testid={`select-edit-group-members-${index}-${gIdx}`}
                                      >
                                        <Users className="h-3 w-3 mr-1 shrink-0" />
                                        {group.employeeIds.length === 0
                                          ? "Selecionar membros..."
                                          : `${group.employeeIds.length} membro(s)`}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-72 p-0" align="start">
                                      <div className="max-h-48 overflow-y-auto p-1">
                                        {people?.map((person) => (
                                          <label
                                            key={person.id}
                                            className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                                          >
                                            <Checkbox
                                              checked={group.employeeIds.includes(person.id)}
                                              onCheckedChange={(checked) => {
                                                const updated = [...editableStages];
                                                const newGroups = [
                                                  ...updated[index].interviewerGroups,
                                                ];
                                                if (checked) {
                                                  newGroups[gIdx] = {
                                                    ...newGroups[gIdx],
                                                    employeeIds: [
                                                      ...newGroups[gIdx].employeeIds,
                                                      person.id,
                                                    ],
                                                  };
                                                } else {
                                                  newGroups[gIdx] = {
                                                    ...newGroups[gIdx],
                                                    employeeIds: newGroups[gIdx].employeeIds.filter(
                                                      (id) => id !== person.id,
                                                    ),
                                                  };
                                                }
                                                updated[index] = {
                                                  ...updated[index],
                                                  interviewerGroups: newGroups,
                                                };
                                                setEditableStages(updated);
                                              }}
                                            />
                                            <span className="text-sm flex-1">
                                              {person.fullName}
                                            </span>
                                          </label>
                                        ))}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                  {group.employeeIds.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {group.employeeIds.map((empId) => {
                                        const person = people?.find((p) => p.id === empId);
                                        return person ? (
                                          <Badge
                                            key={empId}
                                            variant="secondary"
                                            className="text-xs"
                                          >
                                            {person.fullName}
                                          </Badge>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                  <div className="space-y-1">
                                    <Input
                                      type="text"
                                      placeholder="Link de agendamento do grupo (opcional)"
                                      value={group.schedulingLink || ""}
                                      onChange={(e) => {
                                        const updated = [...editableStages];
                                        const newGroups = [...updated[index].interviewerGroups];
                                        newGroups[gIdx] = {
                                          ...newGroups[gIdx],
                                          schedulingLink: e.target.value,
                                        };
                                        updated[index] = {
                                          ...updated[index],
                                          interviewerGroups: newGroups,
                                        };
                                        setEditableStages(updated);
                                      }}
                                      className="text-xs"
                                      data-testid={`input-edit-group-link-${index}-${gIdx}`}
                                    />
                                    {!group.schedulingLink && (
                                      <p className="text-xs text-[hsl(var(--amber-state))] flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        Sem link de agendamento — email não poderá ser enviado
                                      </p>
                                    )}
                                  </div>
                                  {group.employeeIds.length > 0 && group.employeeIds.length < 2 && (
                                    <p className="text-xs text-[hsl(var(--amber-state))] flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Grupo precisa de pelo menos 2 pessoas
                                    </p>
                                  )}
                                </div>
                              </Card>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => {
                                const updated = [...editableStages];
                                updated[index] = {
                                  ...updated[index],
                                  interviewerGroups: [
                                    ...(updated[index].interviewerGroups || []),
                                    { employeeIds: [], schedulingLink: null },
                                  ],
                                };
                                setEditableStages(updated);
                              }}
                              data-testid={`button-add-edit-group-${index}`}
                            >
                              <Users className="h-3 w-3 mr-1" />
                              Adicionar Grupo
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Material de Apoio</Label>
                          <Textarea
                            value={stage.materials || ""}
                            onChange={(e) => {
                              const updated = [...editableStages];
                              updated[index] = {
                                ...updated[index],
                                materials: e.target.value || null,
                              };
                              setEditableStages(updated);
                            }}
                            placeholder="Links, instruções, roteiro de entrevista..."
                            className="min-h-[60px] text-sm mt-1"
                            data-testid={`input-edit-stage-materials-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Formulário de feedback</Label>
                          <p className="text-xs text-muted-foreground mb-1.5">
                            Padrão = GCA, Role-Related Knowledge e COMP Way. Formulários
                            personalizados são criados na aba "Formulários de feedback".
                          </p>
                          <Select
                            value={stage.feedbackFormId ?? "default"}
                            onValueChange={(value) => {
                              const updated = [...editableStages];
                              updated[index] = {
                                ...updated[index],
                                feedbackFormId: value === "default" ? null : value,
                              };
                              setEditableStages(updated);
                            }}
                          >
                            <SelectTrigger
                              className="w-full text-sm"
                              data-testid={`select-stage-feedback-form-${index}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">
                                Padrão (GCA, Role-Related Knowledge e COMP Way)
                              </SelectItem>
                              {feedbackForms.map((form) => (
                                <SelectItem key={form.id} value={form.id}>
                                  {form.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setEditableStages([
                      ...editableStages,
                      {
                        stageName: "",
                        stageOrder: editableStages.length + 1,
                        durationMinutes: 30,
                        defaultInterviewerId: null,
                        interviewerIds: [],
                        interviewerGroups: [],
                        hidden: false,
                        materials: null,
                        description: null,
                        feedbackFormId: null,
                      },
                    ]);
                  }}
                  data-testid="button-add-new-stage"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar Etapa
                </Button>
              </div>
              {archivedInterviewStages.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm text-muted-foreground uppercase tracking-wide">
                      Etapas Arquivadas
                    </span>
                    <Badge variant="secondary">{archivedInterviewStages.length}</Badge>
                  </div>
                  {archivedInterviewStages.map((stage) => (
                    <div
                      key={stage.id}
                      className="flex items-center gap-3 p-3 border border-dashed rounded-lg opacity-70 mb-2"
                    >
                      <Archive className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-muted-foreground">{stage.stageName}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onUnarchiveStage(stage.id)}
                        disabled={unarchiveIsPending}
                      >
                        Desarquivar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  data-testid="button-cancel-edit-stages"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={onValidateAndSaveStages}
                  disabled={updateStagesIsPending}
                  data-testid="button-save-stages"
                >
                  {updateStagesIsPending ? "Salvando..." : "Salvar Etapas"}
                </Button>
              </DialogFooter>
            </TabsContent>

            {/* ===== Formulários de feedback tab ===== */}
            <TabsContent
              value="feedback-forms"
              className="flex-1 overflow-y-auto mt-2 focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <div className="space-y-4 pb-4">
                <p className="text-xs text-muted-foreground">
                  Monte formulários de feedback nomeados para esta vaga. Na aba{" "}
                  <span className="font-medium text-foreground">Editar etapas</span>, cada etapa
                  escolhe o formulário Padrão (GCA, Role-Related Knowledge e COMP Way) ou um dos
                  formulários abaixo. Decisão, comentários gerais e excitement aparecem em todos.
                </p>
                {editableForms.map((form, fIdx) => (
                  <Card key={form.id ?? `new-${fIdx}`} className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={form.name}
                        onChange={(e) => updateForm(fIdx, { name: e.target.value })}
                        placeholder="Nome do formulário (ex: Case Study)"
                        className="flex-1 text-sm font-medium"
                        data-testid={`input-feedback-form-name-${fIdx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() =>
                          setEditableForms((prev) => prev.filter((_, i) => i !== fIdx))
                        }
                        data-testid={`button-remove-feedback-form-${fIdx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {form.criteria.map((criterion, cIdx) => (
                        <div
                          key={criterion.id}
                          className="rounded-md border border-border/60 p-2 space-y-2"
                        >
                          <div className="flex items-center gap-2">
                            <Input
                              value={criterion.label}
                              onChange={(e) =>
                                updateFormCriterion(fIdx, cIdx, { label: e.target.value })
                              }
                              placeholder="Nome do critério (ex: Qualidade da solução)"
                              className="flex-1 text-sm"
                              data-testid={`input-form-criterion-label-${fIdx}-${cIdx}`}
                            />
                            <Select
                              value={criterion.scaleType}
                              onValueChange={(value) =>
                                updateFormCriterion(
                                  fIdx,
                                  cIdx,
                                  value === "binary"
                                    ? {
                                        scaleType: "binary",
                                        scaleMin: undefined,
                                        scaleMax: undefined,
                                      }
                                    : {
                                        scaleType: "numeric",
                                        scaleMin: criterion.scaleMin ?? 0,
                                        scaleMax: criterion.scaleMax ?? 5,
                                      },
                                )
                              }
                            >
                              <SelectTrigger
                                className="w-28 text-sm shrink-0"
                                data-testid={`select-form-criterion-scale-${fIdx}-${cIdx}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="binary">Binário</SelectItem>
                                <SelectItem value="numeric">Nota</SelectItem>
                              </SelectContent>
                            </Select>
                            {criterion.scaleType === "numeric" && (
                              <div className="flex items-center gap-1 shrink-0">
                                <Input
                                  type="number"
                                  min={0}
                                  max={99}
                                  value={criterion.scaleMin ?? 0}
                                  onChange={(e) =>
                                    updateFormCriterion(fIdx, cIdx, {
                                      scaleMin: Number(e.target.value),
                                    })
                                  }
                                  className="w-16 text-sm"
                                  aria-label="Mínimo"
                                  data-testid={`input-form-criterion-min-${fIdx}-${cIdx}`}
                                />
                                <span className="text-xs text-muted-foreground">a</span>
                                <Input
                                  type="number"
                                  min={1}
                                  max={100}
                                  value={criterion.scaleMax ?? 5}
                                  onChange={(e) =>
                                    updateFormCriterion(fIdx, cIdx, {
                                      scaleMax: Number(e.target.value),
                                    })
                                  }
                                  className="w-16 text-sm"
                                  aria-label="Máximo"
                                  data-testid={`input-form-criterion-max-${fIdx}-${cIdx}`}
                                />
                              </div>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                              onClick={() =>
                                updateForm(fIdx, {
                                  criteria: form.criteria.filter((_, j) => j !== cIdx),
                                })
                              }
                              data-testid={`button-remove-form-criterion-${fIdx}-${cIdx}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-3">
                            <Input
                              value={criterion.description ?? ""}
                              onChange={(e) =>
                                updateFormCriterion(fIdx, cIdx, {
                                  description: e.target.value || undefined,
                                })
                              }
                              placeholder="Descrição (opcional, aparece para o entrevistador)"
                              className="flex-1 text-sm"
                              data-testid={`input-form-criterion-description-${fIdx}-${cIdx}`}
                            />
                            <label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap cursor-pointer">
                              <Checkbox
                                checked={!!criterion.requireNotes}
                                onCheckedChange={(checked) =>
                                  updateFormCriterion(fIdx, cIdx, {
                                    requireNotes: checked === true || undefined,
                                  })
                                }
                                data-testid={`checkbox-form-criterion-require-notes-${fIdx}-${cIdx}`}
                              />
                              Comentário obrigatório
                            </label>
                          </div>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full text-xs"
                        disabled={form.criteria.length >= 15}
                        onClick={() =>
                          updateForm(fIdx, {
                            criteria: [
                              ...form.criteria,
                              { id: crypto.randomUUID(), label: "", scaleType: "binary" },
                            ],
                          })
                        }
                        data-testid={`button-add-form-criterion-${fIdx}`}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Adicionar critério
                      </Button>
                    </div>
                  </Card>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={editableForms.length >= 20}
                  onClick={() =>
                    setEditableForms((prev) => [
                      ...prev,
                      {
                        name: "",
                        criteria: [{ id: crypto.randomUUID(), label: "", scaleType: "binary" }],
                      },
                    ])
                  }
                  data-testid="button-add-feedback-form"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar formulário
                </Button>
              </div>
              <DialogFooter className="mt-2">
                <Button
                  onClick={validateAndSaveForms}
                  disabled={saveFormsMutation.isPending}
                  data-testid="button-save-feedback-forms"
                >
                  {saveFormsMutation.isPending ? "Salvando..." : "Salvar formulários"}
                </Button>
              </DialogFooter>
            </TabsContent>

            {/* ===== Configurações tab ===== */}
            <TabsContent
              value="settings"
              className="flex-1 overflow-y-auto mt-2 focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <TooltipProvider delayDuration={200}>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                      <Eye className="w-3.5 h-3.5" />
                      Visibilidade
                    </h4>
                    <div
                      className="flex items-center justify-between"
                      data-testid="toggle-careers-visibility"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">Página de carreiras</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            Controla se esta vaga aparece na página pública de carreiras. Quando
                            oculta, candidatos não conseguem se inscrever por lá.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Switch
                        checked={careersVisibility.checked}
                        onCheckedChange={careersVisibility.onToggle}
                        disabled={careersVisibility.isPending}
                        data-testid={careersVisibility.testId}
                      />
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                      <Sparkles className="w-3.5 h-3.5" />
                      Automação
                    </h4>
                    <div
                      className="flex items-center justify-between"
                      data-testid="toggle-auto-advance"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">Avanço automático</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            Quando ativado, o candidato avança automaticamente para a próxima etapa
                            após todos os entrevistadores aprovarem. No modo manual, é necessário
                            mover manualmente.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Switch
                        checked={autoAdvance.checked}
                        onCheckedChange={autoAdvance.onToggle}
                        disabled={autoAdvance.isPending}
                        data-testid={autoAdvance.testId}
                      />
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                      <Bell className="w-3.5 h-3.5" />
                      Notificações
                    </h4>
                    <div className="space-y-3">
                      <div
                        className="flex items-center justify-between"
                        data-testid="toggle-notify-hm-feedback"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">Alerta de feedback</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              Envia uma notificação no Slack para o hiring manager sempre que um
                              entrevistador submeter feedback sobre um candidato desta vaga.
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Switch
                          checked={feedbackAlert.checked}
                          onCheckedChange={feedbackAlert.onToggle}
                          disabled={feedbackAlert.isPending}
                          data-testid={feedbackAlert.testId}
                        />
                      </div>
                      <div
                        className="flex items-center justify-between"
                        data-testid="toggle-notify-hm-digest"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">Digest diário</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              Envia um resumo diário no Slack para o hiring manager com candidatos
                              que precisam de atenção (aguardando triagem, entrevista pendente,
                              decisão, etc).
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Switch
                          checked={dailyDigest.checked}
                          onCheckedChange={dailyDigest.onToggle}
                          disabled={dailyDigest.isPending}
                          data-testid={dailyDigest.testId}
                        />
                      </div>
                      {dailyDigest.checked && (
                        <div className="mt-3 pl-1">
                          <p className="text-xs text-muted-foreground mb-2">
                            Horários de envio (BRT)
                          </p>
                          <div className="grid grid-cols-6 gap-1">
                            {[
                              0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
                              20, 21, 22, 23,
                            ].map((h) => {
                              const entry = dailyDigest.schedule.find((s) => s.hour === h);
                              const isSelected = !!entry;
                              return (
                                <button
                                  key={h}
                                  type="button"
                                  disabled={dailyDigest.isSchedulePending}
                                  onClick={() => {
                                    if (isSelected && entry) {
                                      dailyDigest.onDeleteEntry(entry.id);
                                    } else {
                                      dailyDigest.onAddHour(h);
                                    }
                                  }}
                                  className={`text-xs rounded-md px-1.5 py-1 border transition-colors ${
                                    isSelected
                                      ? "bg-primary text-primary-foreground border-primary"
                                      : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                                  data-testid={`digest-hour-chip-${h}`}
                                >
                                  {String(h).padStart(2, "0")}h
                                </button>
                              );
                            })}
                          </div>
                          {dailyDigest.hasMultipleJobs && (
                            <button
                              type="button"
                              disabled={dailyDigest.isApplyToAllPending}
                              onClick={dailyDigest.onApplyToAll}
                              className="mt-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors disabled:opacity-50"
                              data-testid="button-apply-digest-to-all"
                            >
                              {dailyDigest.isApplyToAllPending
                                ? "Aplicando..."
                                : "Aplicar a todas as minhas vagas"}
                            </button>
                          )}
                        </div>
                      )}
                      <div
                        className="flex items-center justify-between"
                        data-testid="toggle-notify-hm-new-application"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">Alerta de nova candidatura</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              Envia uma notificação no Slack para o hiring manager sempre que um
                              novo candidato se inscrever nesta vaga.
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Switch
                          checked={newApplicationAlert.checked}
                          onCheckedChange={newApplicationAlert.onToggle}
                          disabled={newApplicationAlert.isPending}
                          data-testid={newApplicationAlert.testId}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </TooltipProvider>
            </TabsContent>

            {/* ===== Aprovação de Oferta tab (superadmin only) ===== */}
            {isSuperAdmin && (
              <TabsContent
                value="offer-approval"
                className="flex-1 overflow-y-auto mt-2 focus-visible:ring-0 focus-visible:ring-offset-0"
              >
                <div className="space-y-4 p-1">
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2 mb-1">
                      <FileCheck className="w-4 h-4" />
                      Regras de Aprovação de Carta Oferta
                    </h3>
                    <p className="text-xs text-muted-foreground mb-4">
                      Define quando uma oferta criada para esta vaga exige aprovação manual antes de
                      ser enviada ao candidato.
                    </p>
                    <OfferApprovalRulesEditor rules={draftRules} onChange={setDraftRules} />
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      size="sm"
                      disabled={offerApproval.isPending}
                      onClick={() => offerApproval.onSave(draftRules)}
                      type="button"
                    >
                      {offerApproval.isPending ? "Salvando..." : "Salvar"}
                    </Button>
                  </div>
                </div>
              </TabsContent>
            )}

            {/* ===== Acessos tab ===== */}
            <TabsContent
              value="access"
              className="flex-1 overflow-y-auto mt-2 focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <div className="space-y-5 p-1">
                {/* Hiring managers section */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" />
                    Hiring Managers
                  </h4>

                  {/* Add HM */}
                  <div className="flex gap-2">
                    <SearchableSelect
                      value={selectedEmployeeId}
                      onValueChange={setSelectedEmployeeId}
                      options={availablePeople.map((p) => ({ value: p.id, label: p.fullName }))}
                      placeholder="Adicionar colaborador..."
                    />
                    <Button
                      size="sm"
                      onClick={() => selectedEmployeeId && addHmMutation.mutate(selectedEmployeeId)}
                      disabled={!selectedEmployeeId || addHmMutation.isPending}
                      aria-label="Adicionar hiring manager"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* HM list */}
                  <div className="space-y-1">
                    {hmLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
                    {!hmLoading && hiringManagers.length === 0 && (
                      <p className="text-sm text-muted-foreground py-1">
                        Nenhum hiring manager configurado.
                      </p>
                    )}
                    {hiringManagers.map((hm) => (
                      <div
                        key={hm.employeeId}
                        className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">{hm.fullName}</span>
                          {hm.isOwner && (
                            <Crown
                              className="w-3 h-3 text-muted-foreground"
                              aria-label="Dono da vaga"
                            />
                          )}
                        </div>
                        {!hm.isOwner && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            aria-label={`Remover ${hm.fullName} como hiring manager`}
                            onClick={() => removeHmMutation.mutate(hm.employeeId)}
                            disabled={removingEmployeeId === hm.employeeId}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* External access section */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <UserPlus className="w-3.5 h-3.5" />
                    Acessos Externos
                    <span className="text-xs font-normal text-muted-foreground">
                      — visualização apenas
                    </span>
                  </h4>

                  <div className="space-y-2">
                    <Label htmlFor="external-email">Adicionar por email</Label>
                    <div className="flex gap-2">
                      <Input
                        id="external-email"
                        type="email"
                        placeholder="email@exemplo.com"
                        value={extEmail}
                        onChange={(e) => {
                          setExtEmail(e.target.value);
                          if (extEmailError) setExtEmailError("");
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleAddExt()}
                      />
                      <Button
                        onClick={handleAddExt}
                        disabled={!extEmail.trim() || addExtMutation.isPending}
                        size="sm"
                        aria-label="Adicionar acesso externo"
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                    </div>
                    {extEmailError && <p className="text-sm text-destructive">{extEmailError}</p>}
                  </div>

                  <div className="space-y-1">
                    {extLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
                    {!extLoading && externalEntries.length === 0 && (
                      <p className="text-sm text-muted-foreground py-1">
                        Nenhum acesso externo configurado.
                      </p>
                    )}
                    {externalEntries.map((entry) => (
                      <div
                        key={entry.userId}
                        className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50"
                      >
                        <span className="text-sm">{entry.email}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          aria-label={`Revogar acesso de ${entry.email}`}
                          onClick={() => revokeExtMutation.mutate(entry.userId)}
                          disabled={revokingUserId === entry.userId}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Archive stage confirmation dialog */}
      <Dialog
        open={!!archiveDialogStage}
        onOpenChange={(dialogOpen) => {
          if (!dialogOpen) setArchiveDialogStage(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Arquivar &ldquo;{archiveDialogStage?.name}&rdquo;</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Esta etapa possui {archiveInfoMutation.data?.interviewCount ?? 0} entrevista(s)
            {(archiveInfoMutation.data?.applicationCount ?? 0) > 0 &&
              ` e ${archiveInfoMutation.data?.applicationCount} candidato(s) nesta etapa`}
            .
          </p>
          <div className="space-y-3">
            <label
              className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer"
              data-selected={archiveAction === "keep"}
            >
              <input
                type="radio"
                checked={archiveAction === "keep"}
                onChange={() => setArchiveAction("keep")}
                className="mt-1"
              />
              <div>
                <div className="font-medium">Manter na etapa arquivada</div>
                <div className="text-sm text-muted-foreground">
                  As entrevistas ficam vinculadas a esta etapa e visíveis no histórico
                </div>
              </div>
            </label>
            <label
              className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer"
              data-selected={archiveAction === "migrate"}
            >
              <input
                type="radio"
                checked={archiveAction === "migrate"}
                onChange={() => setArchiveAction("migrate")}
                className="mt-1"
              />
              <div>
                <div className="font-medium">Migrar para outra etapa</div>
                <div className="text-sm text-muted-foreground">
                  Move entrevistas e candidatos para uma etapa ativa
                </div>
              </div>
            </label>
            {archiveAction === "migrate" && (
              <Select value={archiveTargetStageId} onValueChange={setArchiveTargetStageId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar etapa destino" />
                </SelectTrigger>
                <SelectContent>
                  {editableStages
                    .filter((s) => s.id && s.id !== archiveDialogStage?.id && !s.hidden)
                    .map((s) => (
                      <SelectItem key={s.id} value={s.id!}>
                        {s.stageName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialogStage(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={archiveAction === "migrate" && !archiveTargetStageId}
              onClick={() => {
                if (archiveDialogStage) {
                  onArchiveStage({
                    stageId: archiveDialogStage.id,
                    action: archiveAction,
                    targetStageId: archiveAction === "migrate" ? archiveTargetStageId : undefined,
                  });
                }
              }}
            >
              Arquivar Etapa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
